import { useAuth0 } from '@auth0/auth0-react';
import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { DialogWithForm } from '@strata/react-dialog';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';

import { retrieveAndSetFmhToken } from 'services/authHelpers';
import { updatePayerPendo } from 'services/pendoUtils';
import { AppRoutes } from 'services/routes';
import { SocketTypes, wsConnect, wsDisconnect } from 'services/socketHelpers';
import { resetStore, useAppDispatch, useAppSelector } from 'store';
import { useGetUserProfileQuery, usePutDefaultPayerMutation } from 'store/api/authApi';
import { useGetPayerNameMapQuery } from 'store/api/referenceApi';

const ValidationSchema = Yup.object({
    payer: Yup.object({ payerId: Yup.string().required(), name: Yup.string() }).required(
        validationStrings.requiredField.errorMessage,
    ),
    default: Yup.bool(),
});

interface SwitchPayerDialogProps {
    isOpen: boolean;
    onSecondaryButtonClick: () => void;
}

export const SwitchPayerDialog = ({ isOpen, onSecondaryButtonClick }: SwitchPayerDialogProps) => {
    const navigate = useNavigate();
    const { getAccessTokenSilently, user } = useAuth0();
    const dispatch = useAppDispatch();
    const { payerId } = useAppSelector((state) => state.auth);
    const { data: userProfile, isFetching: isProfileFetching } = useGetUserProfileQuery(undefined, {
        skip: !isOpen,
    });
    const { data: payerNameMap, isFetching: isPayerNameFetching } = useGetPayerNameMapQuery(
        undefined,
        { skip: !isOpen },
    );
    const [putDefaultPayer] = usePutDefaultPayerMutation();

    const mapPayerOption = (payer: string) => ({
        name: payerNameMap?.[payer] ?? payer,
        payerId: payer,
    });

    return (
        <Formik
            initialValues={
                {
                    payer: payerId ? mapPayerOption(payerId) : undefined,
                    default: false,
                } as Yup.InferType<typeof ValidationSchema>
            }
            validationSchema={ValidationSchema}
            onSubmit={async (values) => {
                navigate(AppRoutes.home);
                dispatch(wsDisconnect({ socketType: SocketTypes.NOTIFICATION }));
                const fmhTokenResult = await retrieveAndSetFmhToken({
                    getAccessTokenSilently,
                    dispatch,
                    payerId: values.payer.payerId,
                });
                updatePayerPendo(fmhTokenResult, user);
                dispatch(resetStore());
                dispatch(wsConnect({ socketType: SocketTypes.NOTIFICATION }));
                SuccessToast({ message: `Payer switched to ${values.payer.name}` });
                if (values.default) {
                    await putDefaultPayer({
                        payerId: values.payer.payerId,
                    }).unwrap();
                    SuccessToast({ message: 'Default payer setting updated successfully.' });
                }
                onSecondaryButtonClick();
            }}
            enableReinitialize
        >
            {(formik) => (
                <DialogWithForm
                    isOpen={isOpen}
                    onSecondaryButtonClick={onSecondaryButtonClick}
                    dialogTitle="Switch Payer"
                    secondaryButtonLabel="Cancel"
                    primaryButtonLabel="Switch Payer"
                    isPrimaryButtonDisabled={
                        !formik.isValid || formik.isSubmitting || !formik.dirty
                    }
                    dialogContent={
                        <>
                            <SuggestSingleSelectFormik
                                id="payer"
                                label="Payer"
                                required
                                isLoading={isPayerNameFetching || isProfileFetching}
                                values={Object.keys(userProfile?.payers ?? {})?.map(mapPayerOption)}
                            />
                            <CheckboxListFormik
                                name="default"
                                options={['Set as default on login']}
                                inline
                                values={formik.values.default ? ['Set as default on login'] : []}
                                onChange={(values) => {
                                    void formik.setFieldValue('default', values.length === 1);
                                }}
                            />
                        </>
                    }
                />
            )}
        </Formik>
    );
};
